<template>
  <Dialog
    :dialog-title="t(dialogTypeLabel)"
    :has-footer="false"
    :has-exit-button="true"
    @cancel="$emit('cancel')"
  >
    <div class="mt-6 mx-6 flex flex-1 gap-6 justify-between">
      <div class="flex flex-col gap-[80px]">
        <div class="w-60 flex flex-col gap-4">
          <SingleSelect
            :options="translatedMetricDropdown"
            :selected="metricSelection"
            :label="t('dialog.chartEditor.metric')"
            :border-class="'border-accent-300'"
            :scrollable="true"
            @option-selected="(opt) => (metricSelection = opt)"
          />
          <SingleSelect
            :options="translatedSliceByDropdown"
            :selected="sliceBySelection"
            :label="t('dialog.chartEditor.by')"
            :border-class="'border-accent-300'"
            :scrollable="true"
            @option-selected="(opt) => (sliceBySelection = opt)"
          />
          <TextInput
            :label="t('dialog.chartEditor.chartTitle')"
            :text-prop="modifiedTitle"
            :error-message="chartTitleErrorMessage"
            @input="(val) => (localTitle = val)"
          />
          <IconSelect
            :label="t('dialog.chartEditor.chartType')"
            :icon-list="allowedChartIcons"
            :selected="selectedIcon"
            @icon-selected="setChartType"
          />
        </div>
        <div class="flex items-center gap-4">
          <PrimaryButton :variant="'secondary'" @on-click="$emit('cancel')">
            {{ t("common.cancel") }}
          </PrimaryButton>
          <PrimaryButton
            :disabled="!chartTitleValidator"
            @on-click="handleConfirm"
          >
            {{ t(dialogTypeLabel) }}
          </PrimaryButton>
        </div>
      </div>
      <div class="flex w-[600px] min-h-[456px]">
        <CustomChart
          ref="chartPreview"
          class="w-full min-w-[507px] !p-0"
          :chart-id="chartId"
          :chart-size="chartSize"
          :show-chart-wrapper="false"
          :series="modifiedSeries"
          :type="modifiedType"
          :metric="metricSelection.key"
          :slice-by="sliceBySelection.key"
          :title="modifiedTitle"
          :tooltip-text="modifiedTooltip"
        />
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, Ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { type ListItem } from "@airpr/360-ui-components";
import SingleSelect from "~/components/shared/SingleSelect.vue";
import TextInput from "~/components/shared/TextInput.vue";
import Dialog from "~/components/shared/custom-dialog/CustomDialog.vue";
import PrimaryButton from "~/components/shared/primary-button/PrimaryButton.vue";
import CustomChart from "~/components/shared/CustomChart.vue";
import IconSelect from "~/components/shared/IconSelect.vue";
import {
  ChartType,
  ChartMetric,
  ChartSliceBy,
  AddChartPayload,
} from "~/types/ChartsType";
import { processSeries } from "~/utils/ChartBuilderUtils/index";
import { useSearchStore } from "~/store/search.ts";
import { useChartsStore } from "~/store/charts.ts";
import {
  savedSearchMetricDropdown,
  comparisonSetMetricDropdown,
  savedSearchSliceByDropdowns,
  comparisonSetSliceByDropdowns,
  chartTypeMap,
} from "~/constants/chartDropdowns";

/*
  This component allows you to edit axes of a chart, its type, title, etc.
  Defaults may be passed into this component, but a local state also keeps track
  of changes. Changes can be saved (from the parent component) or discarded.
  initialSeries is no longer required because ProcessSeries will generate the series.
  If save is chosen, the modified chart parameters will be handled with api requests
  here in handleConfirm function
*/

const props = defineProps({
  dialogType: {
    type: String,
    default: "edit",
    validator: (value: string) => ["edit", "add"].includes(value),
  },
  initialSeries: {
    type: Object,
    default: null,
  },
  initialMetric: {
    type: Object,
    default: () => {},
  },
  initialSliceBy: {
    type: Object,
    default: () => {},
  },
  chartId: {
    type: Number,
    required: true,
  },
  chartSize: {
    type: Number,
    required: true,
  },
  chartType: {
    type: String,
    required: true,
  },
  chartTitle: {
    type: String,
    required: true,
  },
  chartTooltip: {
    type: String,
    required: true,
  },
});

const savedSearchStore = useSearchStore();
const chartsStore = useChartsStore();
const emit = defineEmits(["cancel", "add"]);

const { t } = useI18n();

const chartModified: Ref<boolean> = ref(false);
const chartPreview: Ref<any> = ref(null); // a template ref
const localTitle: Ref<string> = ref(props.chartTitle);
const localTooltip: Ref<string> = ref(props.chartTooltip);
const localType: Ref<string> = ref(props.chartType);

const chartTypeToIconMap = ref({
  line: "chart-line",
  pie: "chart-pie",
  column: "chart-column",
  stackedcolumn: "bars-progress",
  packedbubble: "chart-scatter-bubble",
  bar: "chart-bar",
  map: "earth-europe",
});

const chartTitleValidator = computed(() => {
  return localTitle.value.trim().length > 0;
});

const chartTitleErrorMessage = computed(() => {
  return !chartTitleValidator.value
    ? t("dialog.chartEditor.chartTitleEmptyError")
    : "";
});

const translatedMetricDropdown = computed<ListItem[]>(() => {
  const dropdownOption = savedSearchStore.selectedSearch?.is_comparison
    ? comparisonSetMetricDropdown
    : savedSearchMetricDropdown;

  return dropdownOption.map((item): ListItem => {
    return {
      id: item.id,
      name: t(`dialog.chartEditor.metricsDropdown.${item.name}`),
      key: item.name as ChartMetric,
    };
  });
});

const localMetricSelection = ref(props.initialMetric);
const metricSelection = computed<ListItem>({
  get: () => {
    return (
      translatedMetricDropdown.value.find(
        (e) => e.key === localMetricSelection.value?.key,
      ) || translatedMetricDropdown.value[0]
    );
  },
  set: (value: ListItem) => {
    localMetricSelection.value = {
      id: value.id,
      name: value.name,
      key: value.key as ChartMetric,
    };
    localSliceBySelection.value = {
      id: sliceByDropdown.value[0].id,
      name: sliceByDropdown.value[0].name,
      key: sliceByDropdown.value[0].name,
    };
  },
});

const sliceByDropdown = computed(() => {
  if (savedSearchStore.selectedSearch?.is_comparison) {
    return comparisonSetSliceByDropdowns[
      metricSelection.value.key as keyof typeof comparisonSetSliceByDropdowns
    ];
  }
  return savedSearchSliceByDropdowns[
    metricSelection.value.key as keyof typeof savedSearchSliceByDropdowns
  ];
});

const translatedSliceByDropdown = computed<ListItem[]>(() => {
  if (sliceByDropdown.value) {
    return sliceByDropdown.value.map((item): ListItem => {
      return {
        id: item.id,
        name: t(`dialog.chartEditor.sliceByDropdown.${item.name}`),
        key: item.name as ChartSliceBy,
      };
    });
  }
  return [];
});

const localSliceBySelection = ref(props.initialSliceBy);
const sliceBySelection = computed<ListItem>({
  get: () => {
    return (
      translatedSliceByDropdown.value.find(
        (e) => e.key === localSliceBySelection.value?.key,
      ) || translatedSliceByDropdown.value[0]
    );
  },
  set: (value: ListItem) => {
    localSliceBySelection.value = {
      id: value.id,
      name: value.name,
      key: value.key as ChartSliceBy,
    };
  },
});

const allowedChartIcons = computed(() => {
  // Thank you TS, this looks great
  const chartTypes =
    chartTypeMap[metricSelection.value.key as keyof typeof chartTypeMap] || {};
  const sliceTypes: string[] =
    chartTypes[sliceBySelection.value?.key as keyof typeof chartTypes] || [];
  return sliceTypes?.map((item: any) => {
    return [
      "far",
      chartTypeToIconMap.value[item as keyof typeof chartTypeToIconMap.value],
    ];
  });
});

const dialogTypeLabel = computed(() => {
  const baseString = "dialog.chartEditor.";

  return props.dialogType === "edit"
    ? `${baseString}editChart`
    : `${baseString}addChart`;
});

const selectedIcon = computed(() => {
  return chartTypeToIconMap.value[
    modifiedType.value as keyof typeof chartTypeToIconMap.value
  ];
});

const modifiedSeries = computed(() => {
  if (chartModified.value) {
    return processSeries(
      metricSelection.value?.key || "mentions", // Emergency fallback to mentions/time
      modifiedType.value,
      sliceBySelection.value?.key || "time",
      modifiedTitle.value,
      chartsStore.dashboardChartsData,
      savedSearchStore.nestedSearches,
      savedSearchStore.selectedNestedSearch.id ?? "-1",
    );
  }
  return props.initialSeries;
});

const modifiedType = computed(() => {
  if (localType.value !== props.chartType) {
    return localType.value as ChartType;
  }
  return props.chartType as ChartType;
});

const modifiedTitle = computed(() => {
  if (localTitle.value !== props.chartTitle) {
    return localTitle.value;
  }
  return props.chartTitle;
});

const modifiedTooltip = computed(() => {
  if (localTooltip.value !== props.chartTooltip) {
    return localTooltip.value;
  }
  return props.chartTooltip;
});

const setChartType = (type: string): void => {
  // If the type is a key of the map, just go ahead and use that
  if (chartTypeToIconMap.value[type as keyof typeof chartTypeToIconMap.value]) {
    localType.value = type;
  } else {
    // Otherwise, we need to reverse the map and find the right key
    localType.value =
      Object.keys(chartTypeToIconMap.value).find(
        (chartType) =>
          chartTypeToIconMap.value[
            chartType as keyof typeof chartTypeToIconMap.value
          ] === type,
      ) || "chart-line";
  }
};

const generateTranslations = () => {
  // TODO: add titles for other chart types when they are available
  const prefix = `analytics_chart.${localMetricSelection.value.key}.`;
  let suffix;
  switch (localSliceBySelection.value.key) {
    case "time":
      suffix = "over_time";
      break;
    case "media_type":
      suffix = "by_media_type";
      break;
    case "snapshot":
      suffix = "snapshot";
      break;
    case "competitors":
      suffix = "competitors";
      break;
  }
  return `${prefix}${suffix}`;
};

const resetTitle = () => {
  const prefix = generateTranslations();
  localTitle.value = t(`${prefix}.title`);
};

const resetTooltip = () => {
  const prefix = generateTranslations();
  localTooltip.value = t(`${prefix}.tooltip`);
};

const saveLocalTitle = () => {
  if (t(`${generateTranslations()}.title`) !== localTitle.value) {
    return localTitle.value;
  }
  return `${generateTranslations()}.title`;
};

const handleConfirm = () => {
  const chartTitle = saveLocalTitle();
  const payload: AddChartPayload = {
    props: {
      chartTitle,
      chartTooltip: `${generateTranslations()}.tooltip`,
      chartType: localType.value,
      chartSize: props.chartSize,
      dialogType: props.dialogType,
    },
    newMetricSelection: metricSelection,
    newSliceBySelection: sliceBySelection,
    chartId: props.chartId,
  };

  emit("add", payload);
};

watch(
  () => [localMetricSelection.value, localSliceBySelection.value],
  () => {
    // If the axes change, we need a new default title for the chart
    resetTitle();
    resetTooltip();
  },
);

watch(
  () => [
    metricSelection.value,
    sliceBySelection.value,
    modifiedType.value,
    modifiedTitle.value,
  ],
  () => {
    chartModified.value = true;
    chartPreview.value?.redraw();
  },
);

watch(
  () => allowedChartIcons.value,
  () => {
    setChartType(allowedChartIcons.value[0][1]);
  },
);
</script>
